import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import { RootState } from 'reducers';
import { Dimmer, Loader, WppInlineMessage, WppGrid } from 'buildingBlocks';
import { GENERIC_ERROR_MSG } from 'constantsBase';
import { setOptions } from 'containers/StrategyAnalytics/actions';
import { optionsUpdated } from 'containers/StrategyAnalytics/constants/actionsConstants';
import { Flight } from 'utils/types';
import { useMount } from 'utils/hooks/generic/hookWrappers';
import HeliosAnalytics from './HeliosAnalytics';
import { fetchHeliosAnalytics, resetState } from '../actions';
import { REPORT_TYPES } from '../constants';

const reduxState = (state: RootState) => {
  const strategy = state.strategyAnalytics.metadata.strategy;
  const loading = !strategy;

  return {
    strategy,
    loading,
    flights: _.keyBy(_.get(state, 'strategyAnalytics.metadata.flights', []), 'externalId'),
    flightSelected: state.strategyAnalytics.optionValue.flight,

    analytics: state.strategyAnalytics.helios.analytics,
    headers: state.strategyAnalytics.helios.headers,

    loadingAnalytics: state.strategyAnalytics.helios.loading,
    errorAnalytics: state.strategyAnalytics.helios.error,
    noData: state.strategyAnalytics.helios.noData,
  };
};

const Helios: React.FC = (): React.ReactElement => {
  const {
    strategy,
    noData,
    analytics,
    errorAnalytics,
    flightSelected,
    flights,
    headers,
    loading,
    loadingAnalytics,
  } = useSelector(reduxState);
  const dispatch = useDispatch();

  useMount(() => {
    if (flightSelected) {
      dispatch(fetchHeliosAnalytics({
        strategy,
        flight: flights[flightSelected],
        reportType: REPORT_TYPES.BUCKETED,
      }));
    }
    return () => {
      dispatch(resetState());
    };
  });

  const flightChanged = (flight: Flight) => {
    dispatch(setOptions(optionsUpdated.flight, flight.externalId));
    dispatch(fetchHeliosAnalytics({ strategy, reportType: REPORT_TYPES.BUCKETED, flight }));
  };

  return (
    <Dimmer.Dimmable dimmed={loading || loadingAnalytics}>
      <Dimmer active={loading || loadingAnalytics}>
        <Loader>Loading DSP Object data</Loader>
      </Dimmer>
      <WppGrid container fullWidth style={{ marginTop: 24 }}>
        {
          _.isEmpty(flights)
            ? (
              <WppGrid item all={24}>
                <WppInlineMessage
                  size="m"
                  message="This strategy does not have any attached DSP Objects"
                  type="warning"
                />
              </WppGrid>
            )
            : (
              <WppGrid item all={24}>
                <WppGrid container fullWidth>
                  <WppGrid item all={24}>
                    <HeliosAnalytics
                      disabled={errorAnalytics || noData || _.isEmpty(analytics)}
                      flight={_.get(flights, flightSelected) || {}}
                      strategy={strategy}
                      analytics={analytics}
                      headers={headers}
                      flights={flights}
                      flightSelected={flightSelected}
                      flightChange={(flight) => flightChanged(flight)}
                    />
                  </WppGrid>
                  <WppGrid item all={24}>
                    {errorAnalytics && (
                      <WppInlineMessage size="m" message={GENERIC_ERROR_MSG} type="error" />
                    )}
                    {noData && (
                      <WppInlineMessage
                        size="m"
                        message="This DSP Object doesn&apos;t have any data currently, please try again later."
                        type="warning"
                      />
                    )}
                  </WppGrid>
                </WppGrid>
              </WppGrid>
            )
        }
      </WppGrid>
    </Dimmer.Dimmable>
  );
};

export default Helios;
